import { Walking } from 'model/NewMultipleBooking';
import { Moment } from 'moment-timezone';
import { pickedSessionSlotsOnDay, SessionDateSlot } from 'utils/sessionDateSlots';

export const mergeWalkingDayWithClock = (day: Moment, clock: Moment, tz: string): Moment => {
    const d = day.clone().tz(tz).startOf('day');
    const c = clock.clone().tz(tz);
    return d.clone().hour(c.hour()).minute(c.minute()).second(0).millisecond(0);
};

const getWalkingDurationInMinutes = (walking: Walking, fallbackDate: Moment, tz: string): number => {
    if (walking.from && walking.to) {
        const diff = walking.to.clone().tz(tz).diff(walking.from.clone().tz(tz), 'minutes');
        if (diff > 0) {
            return diff;
        }
    }

    const duration = walking.service?.duration;
    if (duration && duration > 0) {
        return duration;
    }

    return 60;
};

const uniqSelectedDays = (selected: Array<Moment>, tz: string): Array<Moment> => {
    const uniqByDay = new Map<string, Moment>();
    selected.forEach(d => {
        const dayStart = d.clone().tz(tz).startOf('day');
        uniqByDay.set(dayStart.format('YYYY-MM-DD'), dayStart);
    });
    return Array.from(uniqByDay.values()).sort((a, b) => a.valueOf() - b.valueOf());
};

const mapSelectedDaysWithClock = (
    selected: Array<Moment>,
    fromClock: Moment,
    toClock: Moment | undefined,
    fallbackDate: Moment,
    walking: Walking,
    tz: string
): Array<SessionDateSlot> => {
    const durationInMinutes = getWalkingDurationInMinutes(walking, fallbackDate, tz);
    const sortedDays = uniqSelectedDays(selected, tz);

    return sortedDays.flatMap((dayStart): Array<SessionDateSlot> => {
        // A date with picked sessions books one appointment per session instead of the typed time.
        const picked = pickedSessionSlotsOnDay(
            walking.sessionSlots,
            dayStart.format('YYYY-MM-DD'),
            durationInMinutes,
            tz
        );
        if (picked.length > 0) {
            return picked;
        }

        const date = mergeWalkingDayWithClock(dayStart, fromClock, tz);
        const dateEnd = toClock
            ? mergeWalkingDayWithClock(dayStart, toClock, tz)
            : date.clone().add(durationInMinutes, 'minutes');
        return [{ date, dateEnd }];
    });
};

export const normalizeWalkingForSlotSave = (walking: Walking, tz: string): Walking => ({
    ...walking,
    from: walking.from?.clone().tz(tz),
    to: walking.to?.clone().tz(tz),
    selectedDates: walking.selectedDates?.map(d => d.clone().tz(tz).startOf('day'))
});

export const getWalkingDateSlotsForSave = (
    walking: Walking,
    fallbackDate: Moment,
    tz: string,
    isEditMode: boolean
): Array<SessionDateSlot> => {
    const normalizedWalking = normalizeWalkingForSlotSave(walking, tz);
    const fallbackInTz = fallbackDate.clone().tz(tz);

    if (isEditMode) {
        const from = (normalizedWalking.from || fallbackInTz).clone().tz(tz);
        const to =
            normalizedWalking.to?.clone().tz(tz) ||
            from.clone().add(getWalkingDurationInMinutes(normalizedWalking, fallbackInTz, tz), 'minutes');
        return [{ date: from, dateEnd: to }];
    }

    const selected = normalizedWalking.selectedDates ?? [];
    const fromClock = (normalizedWalking.from || fallbackInTz).clone().tz(tz);
    const toClock = normalizedWalking.to?.clone().tz(tz);

    if (selected.length === 0) {
        const to =
            toClock ||
            fromClock.clone().add(getWalkingDurationInMinutes(normalizedWalking, fallbackInTz, tz), 'minutes');
        return [{ date: fromClock.clone(), dateEnd: to.clone() }];
    }

    return mapSelectedDaysWithClock(selected, fromClock, toClock, fallbackInTz, normalizedWalking, tz);
};

export const hasMultipleWalkingDates = (walking: Walking, fallbackDate: Moment, tz: string): boolean =>
    getWalkingDateSlotsForSave(walking, fallbackDate, tz, false).length > 1;
